import { useState } from 'react';
import type { EventTemplateBindingItem, Template } from '@/api/templates';
import { Button, ChannelIcon } from '@/components/ui';
import { DispatchModeBadge } from '@/features/templates/dispatchMode';
import { getTechnicalTypeLabel } from '@/features/templates/templateTaxonomy';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ChannelBindingRow } from './ChannelBindingRow';
import {
  collectEventWarnings,
  coverageBadgeClass,
  getEventBindingCoverage,
  slotStatusLabel,
} from './bindingStatus';

type Props = {
  binding: EventTemplateBindingItem;
  defaultOpen: boolean;
  eventBindingSavingByType: Record<string, boolean>;
  getBindingKey: (eventType: string, channel?: string) => string;
  getBindingOptions: (channel?: string) => Template[];
  onSetEventBinding: (eventType: string, templateId: string, channel?: string) => void;
  onClearEventBinding: (eventType: string, channel?: string) => void;
  onOpenTemplate: (templateId: string) => void;
};

export function EventBindingEventPanel({
  binding,
  defaultOpen,
  eventBindingSavingByType,
  getBindingKey,
  getBindingOptions,
  onSetEventBinding,
  onClearEventBinding,
  onOpenTemplate,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const coverage = getEventBindingCoverage(binding);
  const warnings = collectEventWarnings(binding);
  const generic = binding.generic_binding;
  const genericKey = getBindingKey(binding.event_type, generic.channel);
  const genericSaving = !!eventBindingSavingByType[genericKey];
  const genericStatus = slotStatusLabel(generic, 'generic');

  return (
    <div className="overflow-hidden rounded-2xl border border-shell-border bg-shell-surface">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-shell-hover"
      >
        <span className="text-shell-muted">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-shell-text">{binding.label}</div>
          <div className="mt-0.5 font-mono text-[11px] text-shell-muted">{binding.event_type}</div>
        </div>
        <div className="hidden items-center gap-1 sm:flex">
          {(binding.channel_bindings || []).map((slot) => {
            const bound = Boolean(slot.has_binding && slot.template);
            const tip = `${slot.label}: ${bound ? 'override задан' : 'нет override'}`;
            return (
              <span
                key={`${binding.event_type}:${slot.channel || slot.label}`}
                className={`inline-flex rounded-md border p-0.5 ${
                  bound
                    ? 'border-shell-success/30 bg-shell-success-soft/60'
                    : 'border-shell-border bg-shell-elevated/50 opacity-45'
                }`}
                title={tip}
                aria-label={tip}
              >
                <ChannelIcon channelId={slot.channel} size={14} />
              </span>
            );
          })}
        </div>
        <DispatchModeBadge mode={binding.dispatch_mode} />
        <span
          className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${coverageBadgeClass(coverage.kind)}`}
        >
          {coverage.label}
        </span>
      </button>

      {open ? (
        <div className="space-y-3 border-t border-shell-border-soft px-4 py-4">
          <div className="rounded-xl border border-shell-border bg-shell-elevated/60 p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-wide text-shell-muted">
                  Общая привязка
                </div>
                <div
                  className={`mt-1 text-xs ${
                    generic.template
                      ? generic.template.is_active
                        ? 'text-shell-success'
                        : 'text-shell-warning'
                      : 'text-shell-muted'
                  }`}
                >
                  {genericStatus}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {generic.template ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2.5 text-xs font-semibold"
                    onClick={() => onOpenTemplate(generic.template!.id)}
                  >
                    Открыть
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-8 px-2.5 text-xs font-semibold"
                  disabled={genericSaving || !generic.has_binding}
                  onClick={() => onClearEventBinding(binding.event_type, generic.channel)}
                >
                  Очистить
                </Button>
              </div>
            </div>
            <select
              className="w-full rounded-lg border border-shell-border bg-shell-surface px-3 py-2 text-sm text-shell-text outline-none focus:border-shell-primary focus:ring-2 focus:ring-shell-primary/20"
              value={generic.template?.id || ''}
              disabled={genericSaving}
              onChange={(event) => {
                const value = event.target.value;
                if (!value) return;
                onSetEventBinding(binding.event_type, value, generic.channel);
              }}
            >
              <option value="">Выберите generic шаблон</option>
              {getBindingOptions(generic.channel).map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} [{getTechnicalTypeLabel(template)}]
                  {template.is_active ? '' : ' (неактивен)'}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wide text-shell-muted">
              Channel overrides
            </div>
            {(binding.channel_bindings || []).map((slot) => (
              <ChannelBindingRow
                key={`${binding.event_type}:${slot.channel || slot.label}`}
                slot={slot}
                options={getBindingOptions(slot.channel)}
                saving={!!eventBindingSavingByType[getBindingKey(binding.event_type, slot.channel)]}
                onSet={(templateId) => onSetEventBinding(binding.event_type, templateId, slot.channel)}
                onClear={() => onClearEventBinding(binding.event_type, slot.channel)}
                onOpen={onOpenTemplate}
              />
            ))}
          </div>

          {warnings.length > 0 ? (
            <div className="rounded-xl border border-shell-warning/30 bg-shell-warning-soft/50 px-3 py-2">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 text-left text-xs font-semibold text-shell-warning"
                onClick={() => setWarningsOpen((value) => !value)}
              >
                <span>
                  {warnings.length === 1
                    ? 'Есть замечание по runtime fallback'
                    : `Замечания runtime (${warnings.length})`}
                </span>
                <span>{warningsOpen ? 'Скрыть' : 'Подробнее'}</span>
              </button>
              {warningsOpen ? (
                <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-shell-warning">
                  {warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 line-clamp-1 text-xs text-shell-warning/90">{warnings[0]}</p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
